import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { LocalizationPipe, NgxValidateCoreModule } from '@abp/ng.core';
import { Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { BaseFormComponent } from 'src/core/forms/base-form.component';
import { FormActionsComponent } from 'src/core/forms/form-actions/form-actions.component';
import { CxInputPendingLoaderDirective } from 'src/app/shared/directives/cx-input-pending-loader.directive';
import { CrudService } from 'src/core/services/crud/crud.service';
import { CrmSegmentDefinitionDto } from '../../models/crm-segment-definition.dto';
import { CrmSegmentDefinitionService } from '../../services/crm-segment-definition.service';

@Component({
  selector: 'app-crm-segment-definitions-form-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LocalizationPipe,
    DialogModule,
    InputTextModule,
    NgxValidateCoreModule,
    CxInputPendingLoaderDirective,
    FormActionsComponent,
  ],
  templateUrl: './crm-segment-definitions-form-panel.component.html',
  styleUrl: './crm-segment-definitions-form-panel.component.scss',
})
export class CrmSegmentDefinitionsFormPanelComponent extends BaseFormComponent<CrmSegmentDefinitionDto> {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly crudService = inject(CrudService);
  private currentSegmentName = '';

  @Input() visible = false;
  @Input() segmentId: number | null = null;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();

  constructor(
    private readonly crmSegmentDefinitionService: CrmSegmentDefinitionService,
    changeDetectorRef: ChangeDetectorRef,
  ) {
    super(changeDetectorRef);
  }

  ngOnChanges(): void {
    this.syncFormFromInputs();
  }

  get isEditMode(): boolean {
    return this.segmentId != null;
  }

  get isValidating(): boolean {
    return this.form?.pending ?? false;
  }

  get isBusy(): boolean {
    return this.isSubmitting || this.isValidating;
  }

  protected buildForm(): FormGroup {
    return this.fb.group({
      name: this.fb.control('', {
        validators: [Validators.required, Validators.maxLength(100)],
        asyncValidators: [this.uniqueNameValidator()],
        updateOn: 'change',
      }),
    });
  }

  private uniqueNameValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = String(control.value ?? '').trim();
      if (!value) {
        return of(null);
      }

      if (
        this.segmentId != null &&
        value.toLocaleLowerCase() === this.currentSegmentName.toLocaleLowerCase()
      ) {
        return of(null);
      }

      return timer(300).pipe(
        switchMap(() =>
          this.crmSegmentDefinitionService.checkNameUnique(value, this.segmentId ?? undefined),
        ),
        map(isUnique => (isUnique ? null : { nameNotUnique: true })),
        catchError(() => of(null)),
        take(1),
      );
    };
  }

  get headerKey(): string {
    return this.isEditMode
      ? 'Settings::CRM:Definitions:EditPanelTitle'
      : 'Settings::CRM:Definitions:AddPanelTitle';
  }

  protected override afterFormInit(): void {
    this.syncFormFromInputs();
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  onCancel(): void {
    this.visibleChange.emit(false);
  }

  onSave(): void {
    if (this.isValidating) {
      return;
    }

    this.onSubmit();
  }

  protected submitRequest(value: CrmSegmentDefinitionDto): Observable<unknown> {
    const payload = {
      name: value.name.trim(),
    };

    if (this.segmentId != null) {
      return this.crudService.update<CrmSegmentDefinitionDto>(CrmSegmentDefinitionDto, {
        id: this.segmentId,
        ...payload,
      });
    }

    return this.crudService.insertWithoutId<CrmSegmentDefinitionDto>(CrmSegmentDefinitionDto, {
      id: 0,
      ...payload,
    });
  }

  protected override onSubmitSuccess(): void {
    super.onSubmitSuccess();
    this.saved.emit();
    this.visibleChange.emit(false);
  }

  private syncFormFromInputs(): void {
    if (!this.visible) {
      return;
    }

    if (this.segmentId == null) {
      this.currentSegmentName = '';
      this.resetForm({ name: '' });
      return;
    }

    this.crudService
      .getById<CrmSegmentDefinitionDto>(CrmSegmentDefinitionDto, this.segmentId)
      .subscribe({
        next: segment => {
          const name = segment?.name ?? '';
          this.currentSegmentName = name.trim();
          this.resetForm({ name });
        },
        error: () => {
          this.currentSegmentName = '';
          this.resetForm({ name: '' });
        },
      });
  }
}
